// Thin fetch wrapper over the custom /v1/* and /api/cloud/* routes. Unlike the
// PocketBase auto-REST collections, these routes need the bearer token attached
// explicitly (authHeader) and return the canonical {code,message} error body on
// failure. Every typed API module is built on these helpers.

import { authHeader } from "./client.js";
import { parseError, type ApiError } from "./errors.js";

/** RequestInit with headers narrowed to a plain string map (which the helpers merge). */
type FetchOpts = Omit<RequestInit, "headers"> & { headers?: Record<string, string> };

/**
 * request performs a fetch against a same-origin API path with the bearer token
 * attached. On a non-2xx response it reads the {code,message} body and throws the
 * canonical ApiError. On success it returns the raw Response (callers that need
 * JSON use requestJSON; callers that need a blob/stream use this directly).
 */
export async function request(path: string, opts: FetchOpts = {}): Promise<Response> {
  const res = await fetch(path, {
    ...opts,
    headers: { ...authHeader(), ...(opts.headers ?? {}) },
  });
  if (!res.ok) throw await errorFromResponse(res);
  return res;
}

/** requestJSON is request() plus a JSON body parse. Use for the JSON routes. */
export async function requestJSON<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  const res = await request(path, opts);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** sendJSON POSTs/PUTs/PATCHes a JSON body and parses the JSON response. */
export function sendJSON<T>(
  path: string,
  method: string,
  body: unknown,
  opts: FetchOpts = {},
): Promise<T> {
  return requestJSON<T>(path, {
    ...opts,
    method,
    headers: { "Content-Type": "application/json", ...(opts.headers ?? {}) },
    body: JSON.stringify(body),
  });
}

/** getJSON is a GET returning parsed JSON. */
export function getJSON<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  return requestJSON<T>(path, { ...opts, method: "GET" });
}

/**
 * optionalJSON is a GET for a route that exists only while a server feature is
 * on. It resolves to null when the route is absent: a 404 or 405, or an answer
 * that is not JSON — an unregistered GET under /v1 falls through to the SPA's
 * index.html with a 200. Any other failure throws the canonical ApiError, so a
 * caller can tell "off" from "broken".
 */
export async function optionalJSON<T>(path: string, opts: FetchOpts = {}): Promise<T | null> {
  const res = await fetch(path, {
    ...opts,
    method: "GET",
    headers: { ...authHeader(), ...(opts.headers ?? {}) },
  });
  if (res.status === 404 || res.status === 405) return null;
  if (!res.ok) throw await errorFromResponse(res);
  if (!(res.headers.get("Content-Type") ?? "").includes("application/json")) return null;
  return (await res.json()) as T;
}

/** query builds a `?a=b&c=d` string from defined params (empty when none). */
export function query(params: Record<string, string | number | undefined | null>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

async function errorFromResponse(res: Response): Promise<ApiError> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = undefined;
  }
  return parseError({ status: res.status, response: body });
}
